import { ColumnDef } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import styles from './ProjectsTable.module.css';
import PlatformStatsBadge from 'Modules/Plans/Project/components/PlatformStatsBadge/PlatformStatsBadge';
import { Skeleton } from '@round/ui-kit';
import { VirtualizedTable, VirtualizedTableProps, assets } from '@round/whitelabel-ui';
import ProjectCell from '../../../components/cells/ProjectCell/ProjectCell';
import BrandCell from '../../../components/cells/BrandCell/BrandCell';
import UpdatedCell from '../../../components/cells/UpdatedCell';
import DurationCell from '../../../components/cells/DurationCell';
import UsersCell from '../../../components/cells/UsersCell/UsersCell';
import GenresCell from '../../../components/cells/GenresCell';
import { useFeatureFlags } from 'utility/featureFlags/FeatureFlagsContext';
import { Dropdown } from '@round/whitelabel-ui';
import SongGrowthChart from 'Modules/Plans/Songs/components/SongGrowthChart/SongGrowthChart';
import { Meta, ProjectsTableRow, TableCellContext, getTableMeta } from './helpers';
import OwnedByCell from './OwnedByCell/OwnedByCell';

const { Trash: TrashIcon } = assets;

type Props = Pick<VirtualizedTableProps<ProjectsTableRow>, 'data' | 'onEndReached'> & {
    meta: Meta;
};

const allPlatforms = ['tiktok', 'instagram', 'youtube'] as const;

const ProjectsTable = ({ data, onEndReached, meta }: Props) => {
    const navigate = useNavigate();
    const { isLoading, hasError } = meta;
    const isProjectSharingEnabled = useFeatureFlags().isEnabled('project_sharing');
    const isGenresEnabled = useFeatureFlags().isEnabled('rou_1766_project_genres');

    const columns: ColumnDef<ProjectsTableRow, any>[] = [
        {
            header: 'Artist',
            accessorKey: 'brand',
            meta: {
                className: styles.brandColumn,
            },
            cell: BrandCell,
        },
        {
            header: 'Project',
            accessorKey: 'name',
            meta: {
                className: styles.projectColumn,
            },
            cell: ProjectCell,
        },
        {
            header: 'Channels',
            accessorKey: 'platforms',
            cell: ({ row: { original }, getValue, table }: TableCellContext<'platforms'>) => {
                const { getIsRowLoading } = getTableMeta(table);

                if (getIsRowLoading(original)) {
                    return <Skeleton />;
                }

                const platforms = getValue() || [];

                return (
                    <div className={styles.channelsContainer}>
                        {allPlatforms.map((platform) => (
                            <PlatformStatsBadge
                                key={platform}
                                projectId={original.id}
                                platform={platform}
                                isActive={platforms.includes(platform)}
                            />
                        ))}
                    </div>
                );
            },
        },
        {
            header: 'Last updated',
            accessorKey: 'associations_updated_at',
            cell: UpdatedCell,
        },
        {
            header: 'Run dates',
            id: 'runDates',
            cell: DurationCell,
        },
        {
            header: 'Planners',
            accessorKey: 'users',
            cell: UsersCell,
        },
        ...(isGenresEnabled
            ? [
                  {
                      header: 'Genres',
                      accessorKey: 'genres',
                      cell: GenresCell,
                  },
              ]
            : []),
        {
            header: 'Song',
            id: 'song',
            meta: {
                className: styles.songColumn,
            },
            accessorKey: 'songStatsTimeSeries',
            cell: ({ getValue, row: { original }, table }: TableCellContext<'songStatsTimeSeries'>) => {
                const { getIsRowLoading, onSongChartClick, isTimeSeriesLoading, isTimeSeriesError } =
                    getTableMeta(table);
                if (getIsRowLoading(original)) {
                    return <Skeleton />;
                }

                if (original.song_id === null) {
                    return '-';
                }

                return (
                    <SongGrowthChart
                        timeSeries={getValue()}
                        isLoading={isTimeSeriesLoading?.(original) ?? false}
                        hasError={isTimeSeriesError?.(original) ?? false}
                        onClick={(e) => {
                            e.stopPropagation();

                            if (!original.song_id) {
                                return;
                            }

                            onSongChartClick(original.song_id);
                        }}
                    />
                );
            },
        },
        {
            header: 'Run by',
            id: 'ownedBy',
            cell: OwnedByCell,
        },
        {
            header: '',
            id: 'actions',
            meta: {
                className: styles.actions,
            },
            cell: ({ row: { original }, table }) => {
                const { getIsRowLoading, onDeleteProject } = getTableMeta(table);

                if (getIsRowLoading(original)) {
                    return <Skeleton />;
                }

                return (
                    <div onClick={(e) => e.stopPropagation()}>
                        <Dropdown>
                            {({ setIsOpen }) => (
                                <>
                                    <Dropdown.Target />
                                    <Dropdown.Menu>
                                        <Dropdown.Item
                                            icon={() => <TrashIcon className={styles.deleteAction} />}
                                            onClick={() => {
                                                onDeleteProject(original);
                                                setIsOpen(false);
                                            }}
                                            className={styles.deleteAction}
                                        >
                                            Delete project
                                        </Dropdown.Item>
                                    </Dropdown.Menu>
                                </>
                            )}
                        </Dropdown>
                    </div>
                );
            },
        },
    ];

    const emptyRows = new Array(10).fill({}) as ProjectsTableRow[];
    const rows = isLoading ? data.concat(emptyRows) : data;

    return (
        <VirtualizedTable
            isLoading={isLoading}
            containerClassName={styles.tableContainer}
            data={rows}
            columns={columns}
            meta={meta}
            columnVisibility={{ ownedBy: isProjectSharingEnabled }}
            onRowClick={(row) => {
                if (row.original.id) {
                    navigate(`${row.original.id}/`);
                }
            }}
            className={styles.table}
            noDataLabel={
                <div aria-label="no projects" className={styles.noDataContainer}>
                    {hasError ? "Couldn't fetch projects" : 'No projects found'}
                </div>
            }
            virtualizerOptions={{
                count: rows.length,
                estimateSize: () => 50,
                getItemKey: (index) => rows[index]?.id || `skeleton-${index}`,
                overscan: 5,
            }}
            onEndReached={onEndReached}
        />
    );
};

export default ProjectsTable;
